import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  Radio,
  RadioGroup,
  Box,
} from '@vimeo/bokeh';
import { ModalHeader } from '@vimeo/bokeh';
import {
  PostVideoCommentReportOptions,
  PostVideoCommentReportFields,
} from '@vimeo/gctl-api/fetchers/video/comment/report';
import {
  PostVideoReportFields,
  PostVideoReportOptions,
} from '@vimeo/gctl-api/fetchers/video/report';
import { translate } from '@vimeo/i18n-runtime';

export const REASON_UNSELECTED = 'unselected';
export type VideoReason = PostVideoReportOptions<PostVideoReportFields>['variables']['reason'];
export type VideoCommentReason =
  PostVideoCommentReportOptions<PostVideoCommentReportFields>['variables']['reason'];
export type Reason = VideoReason | VideoCommentReason | typeof REASON_UNSELECTED;

export interface ReportModalProps {
  header: string;
  cta: string;
  reasons: { title: string; description: string; value: string }[];
  isOpen: boolean;
  isPosting: boolean;
  reportReason: Reason;
  setReportReason: (value: Reason) => void;
  onCloseCleanup: () => void;
  submitReport: () => void;
}

export const ReportModal = ({
  header,
  cta,
  reasons,
  isOpen,
  isPosting,
  reportReason,
  setReportReason,
  onCloseCleanup,
  submitReport,
}: ReportModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onCloseCleanup} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxHeight="calc(100% - 3rem)">
        <ModalHeader>{header}</ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1 1 0%" overflow="auto">
          <Text variant="body-xl" fontWeight="medium" mb="200">
            {cta}
          </Text>
          <RadioGroup onChange={setReportReason} value={reportReason}>
            <Stack spacing={200}>
              {reasons.map((reason) => (
                <Box key={reason.title}>
                  <Radio value={reason.value}>
                    <Text variant="body-md">{reason.title}</Text>
                  </Radio>
                  <Text variant="body-md" pl="300" color="text-secondary">
                    {reason.description}
                  </Text>
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        </ModalBody>
        <ReportModalFooter
          isSubmitDisabled={reportReason == REASON_UNSELECTED || isPosting}
          onClose={onCloseCleanup}
          onSubmit={submitReport}
        />
      </ModalContent>
    </Modal>
  );
};

export interface ReportModalFooterProps {
  isSubmitDisabled: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

const ReportModalFooter = ({ isSubmitDisabled, onClose, onSubmit }: ReportModalFooterProps) => {
  return (
    <ModalFooter border="none" py="200">
      <Text flex="1" variant="body-md" textDecor="underline">
        <a href={'/help/guidelines'}>{translate('Learn more')}</a>
      </Text>
      <Button variant="minimal" size="md" onClick={onClose}>
        {translate('Cancel')}
      </Button>
      <Button variant="primary" size="md" onClick={onSubmit} isDisabled={isSubmitDisabled}>
        {translate('Report')}
      </Button>
    </ModalFooter>
  );
};
